import { useEffect, useState } from 'react';
import { sessionsApi } from '../../../api/sessionsApi';
import { loadTranscriptBlocks } from '../utils/loadTranscriptBlocks';

const NOT_EDITABLE_MESSAGES = {
  PENDING: 'Transcript processing has not started yet.',
  PROCESSING: 'Transcript is still being processed. Please wait until processing is complete.',
  FAILED: 'Transcript processing failed. Please retry processing.',
};

const notEditableMessage = (status) => (NOT_EDITABLE_MESSAGES[status] || 'Transcript is not available for editing yet.')
  + '\n\nTranscript editing is only available when processing is fully completed.';

/**
 * Editor metadata of the session (video, title, status) and its transcript as
 * editor blocks. `error` explains why a transcript cannot be edited yet.
 */
export function useEditableTranscript(sessionId, lang) {
  const [session, setSession] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        // /edit-meta, not /sessions/{id}: the full session carries hundreds of KB of report markdown the editor never reads.
        const meta = await sessionsApi.getEditMeta(sessionId);
        if (cancelled) return;
        setSession(meta);

        const status = meta.transcriptStatus || meta.transcript_status;
        if (status !== 'COMPLETED') {
          setError(notEditableMessage(status));
          return;
        }
        const loaded = await loadTranscriptBlocks(sessionId, lang);
        if (!cancelled) setBlocks(loaded);
      } catch (err) {
        console.error('Error fetching session data:', err);
        if (!cancelled) setError(err.message || 'Failed to load session');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [sessionId, lang]);

  return { session, blocks, loading, error };
}
